import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { updateCart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadPaymetricConfig from "@insite/client-framework/Store/Context/Handlers/LoadPaymetricConfig";
import loadTokenExConfig from "@insite/client-framework/Store/Context/Handlers/LoadTokenExConfig";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import resetCurrentCartId from "@insite/client-framework/Store/Data/Carts/Handlers/ResetCurrentCartId";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import checkoutWithPayPal from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/CheckoutWithPayPal";
import getPaymetricResponsePacket from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/GetPaymetricResponsePacket";
import Tooltip, { TooltipComponentProps, TooltipPresentationProps } from "@NA/MobiusOverrides/Tooltip";
import preloadOrderConfirmationData from "@insite/client-framework/Store/Pages/OrderConfirmation/Handlers/PreloadOrderConfirmationData";
import translate from "@insite/client-framework/Translate";
import { CartModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { CheckoutReviewAndSubmitPageContext } from "@insite/content-library/Pages/CheckoutReviewAndSubmitPage";
import BillingAddressInfoDisplay, {
    BillingAddressInfoDisplayStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/BillingAddressInfoDisplay";

import Button, { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import { FormFieldStyle } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset, GridWrapper } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Radio, { RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps, RadioGroupStyle } from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import loadCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCart";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ADIBillingAddressDisplay from "@NA/Components/SaveCard/ADIBillingAddressDisplay";
import ADICreditCardView from "@NA/Components/SaveCard/ADICreditCardView";
import ADIEditCreditCardModal from "@NA/Components/SaveCard/ADIEditCreditCardModal";
import ADIGetCreditCardModal from "@NA/Components/SaveCard/ADIGetCreditCardModal";
import ADINewCreditCardModal from "@NA/Components/SaveCard/ADINewCreditCardModal";

import {
    ADICreditCardsModelNA, // Being used in P21
    CreditCardDtoCollection,
    getSavedCreditCards,
    GetSavedCreditCardsModel,
    removeCreditCardNANew,
} from "@NA/Services/SavedCreditCardsService";
import ADIColors from "@NA/Utils/ADIColors";
import { cloneDeep } from "lodash";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import checkoutCart from "@NA/Handlers/ADICheckoutCartHandler";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import { IconWrapperProps } from "@insite/mobius/Icon/Icon"; // Being used in P21
import { CustomParameter } from "@NA/Handlers/CheckoutReviewAndSubmit/PlaceOrderExtended";
import { LoadCartApiInterface } from "@NA/Handlers/LoadCartExtended";
import updatePaymentMethod from "@NA/Handlers/ADICheckoutPaymentMethodHandler";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import { itemListStyles } from "@insite/content-library/Widgets/Basic/ItemList";
import setUpdateCartLoading from "@NA/Handlers/ADIUpdateCartLoadingHandler";
import placeOrder from "@NA/Overrides/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrder";
import { isCodCustomer, updateCartCalculationWithNewCart } from "@NA/Utilities/CheckoutUtilities";
import WarningRedIcon from "@NA/Icons/WarningRedIcon";
// From X to CreditCardDetailsEntry imports are being used by P21
import X from "@NA/Icons/X";
import { CartSummaryMethodsResults } from "@NA/Services/ADICartSummaryService";
import ADINewCreditCardModalPopup from "@NA/Components/SaveCard/ADINewCreditCardModalPopup";
import adiGetCreditCardListRecap from "@NA/Handlers/Invoices/ADIGetCreditCardListRecap";
import CreditCardDetailsEntry from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CreditCardDetailsEntry";

const mapStateToProps = (state: CustomApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const settingsCollection = getSettingsCollection(state);
    const currentCart = getCurrentCartState(state).value;
    const initialPaymentMethod = cartState.value?.paymentMethod?.name;
    const billAction = state.ADIGlobalData.shippingMethodStore.updatePaymentMethod;
    const oneHourPickup = state.ADIGlobalData.shippingMethodStore.updateOneHourPickup;
    const deliveryMethodInProgress = state.ADIGlobalData.shippingMethodStore.deliveryMethodInProgress;
    const deliveryMethod = cartState.value?.properties?.deliveryMethod;
    const TaxAndShippingDtls = state.ADIGlobalData.TaxSummaryStore?.CartSummaryMethods; // This is being used by P21
    const deliveryMethods = state.ADIGlobalData.shippingMethodStore?.shippingMethods?.deliveryMethod;

    return {
        cartState,
        billToState: getBillToState(state, cartState.value ? cartState.value.billToId : undefined),
        websiteSettings: settingsCollection.websiteSettings,
        cartSettings: settingsCollection.cartSettings,
        tokenExConfigs: state.context.tokenExConfigs,
        orderConfirmationPageLink: getPageLinkByPageType(state, "OrderConfirmationPage"),
        savedPaymentsPageLink: getPageLinkByPageType(state, "SavedPaymentsPage"),
        session: state.context.session,
        signInPageLink: getPageLinkByPageType(state, "SignInPage"),
        payPalRedirectUri: state.pages.checkoutReviewAndSubmit.payPalRedirectUri,
        location: getLocation(state),
        usePaymetricGateway: settingsCollection.websiteSettings.usePaymetricGateway,
        paymetricConfig: state.context.paymetricConfig,
        enableVat: settingsCollection.productSettings.enableVat,
        adiPaymentCustomizationDto: cartState?.value?.adiPaymentCustomizationDto,
        initialPaymentMethod,
        localCart: state.ADIGlobalData.checkoutCartStore.cart,
        alternateShipAddress: state.ADIGlobalData.checkoutCartStore.alternateShipTo,
        shipToState: getShipToState(state, state.context.session.shipToId),
        billAction,
        oneHourPickup,
        deliveryMethodInProgress,
        updatedDeliveryMethod: state.ADIGlobalData.shippingMethodStore.updateDeliveryMethod,
        deliveryMethod,
        TaxAndShippingDtls: TaxAndShippingDtls, // P21 Change
        creditCardPaymentTypes: state.ADIGlobalData.adiCreditCardListRecapStore.creditCardDetails, // P21 Change
        deliveryMethods,
    };
};

const mapDispatchToProps = {
    loadTokenExConfig,
    placeOrder,
    checkoutWithPayPal,
    preloadOrderConfirmationData,
    loadBillTo,
    loadPaymetricConfig,
    getPaymetricResponsePacket,
    resetCurrentCartId,
    loadCart,
    loadCurrentCart,
    checkoutCart,
    updatePaymentMethod,
    setUpdateCartLoading,
    adiGetCreditCardListRecap, // This is being used in P21
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasHistory &
    HasToasterContext;

export interface CheckoutReviewAndSubmitPaymentDetailsStyles {
    paymentMethodWrapper?: InjectableCss;
    // From iconprops to disabledElement properties are being used in P21
    iconProps?: IconWrapperProps & InjectableCss;
    TooltipBody?: InjectableCss;
    zIndex?: InjectableCss;
    Tooltiptext?: InjectableCss;
    zIndex2?: InjectableCss;
    svgIcons?: InjectableCss;
    input?: TextFieldPresentationProps;
    input1?: TextFieldPresentationProps;
    disabledElement?: InjectableCss;
    form?: InjectableCss;
    fieldset?: InjectableCss;
    heading?: TypographyPresentationProps;
    subHeading?: TypographyPresentationProps;
    adipaymentDtoContainer?: InjectableCss;
    billAndPayStyledWrapper?: InjectableCss;
    billAndPayStyledInnerWrapper?: InjectableCss;
    billAndPayRadioButton?: RadioGroupProps;
    cardDisplyWithButton?: InjectableCss;
    selectNewButtonsWrapper?: InjectableCss;
    mainPaymentGridItem?: GridItemProps;
    rightLinks?: LinkPresentationProps;
    primaryButton?: ButtonPresentationProps;
    secondaryButton?: ButtonPresentationProps;
    lTLPopupModal?: ModalPresentationProps;
    lTlContentContainer?: InjectableCss;
    lTlContent?: InjectableCss;
    lTlBtnContainer?: InjectableCss;
    returnBtn?: ButtonProps;
    placeOrderBtn?: ButtonProps;
    modal?: ModalPresentationProps;
    TaxAndShippingDtls?: CartSummaryMethodsResults; // This is being used in P21
}

// This interface is being used by P21
export interface AdiPaymentCustomizationDto {
    po: {
        visible: boolean;
        isReadonly: boolean;
        isRequired: boolean;
        maxLength: number;
    };
}

// The below style is being used in P21
export const checkoutReviewAndSubmitPaymentDetailsStyles: CheckoutReviewAndSubmitPaymentDetailsStyles = {
    disabledElement: {
        css: css`
            display: none;
        `,
    },
    input: {
        css: css`
            border: 1px solid red;
        `,
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            inputSelect: css`
                border-radius: 3px;
                font-size: 12px;
                width: 100%;
                float: right;
                height: 34px;
                padding: 9px 40px 9px 15px;
                font-weight: 600;
                border: 1px solid ${ADIColors.errorMsgRed} !important;

                :focus-within {
                    padding: 9px 40px 9px 15px;
                    outline-color: red;
                    outline-style: none;
                    outline-width: 0px;
                }
                &:focus {
                    ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                    outline-color: red;
                                    outline-style: none;
                                    outline-width: 0px;
                                `,
                        ],
                        "min",
                    );
                }}
                }
                &:focus-visible {
                    ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                    outline-color: ${ADIColors.darkBlack3};
                                    outline-style: ${getProp("theme.focus.style", "solid")};
                                    outline-width: 1px;
                                    outline-offset: 1px;
                                `,
                        ],
                        "min",
                    );
                }}
                }
                ::placeholder {
                    color: ${ADIColors.errorMsgRed};
                    opacity: 1;
                }

                ::-ms-input-placeholder {
                    color: ${ADIColors.gray3};
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            max-width: 300px;
                        `,
                        css`
                            max-width: 300px;
                        `,
                        css`
                            max-width: 400px;
                        `,
                    ])}
            `,
            formInputWrapper: css`
                display: flex;
                display: flex !important;
                flex-direction: column !important;
                > button {
                    width: 35px !important;
                    height: 35px !important;
                    padding: 4px !important;
                    ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                                right: 0px !important;
                            `,
                        css`
                                right: 0px !important;
                            `,
                        null,
                    ])}
                    > ${IconWrapper} {
                        top: 0px;
                        right: 0px;
                        width: 100%;
                        height: 100%;
                        padding: 4px;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            max-width: 338.63px;
                        `,
                    ])}
                @media only screen and (max-width: 1700px) {
                    max-width: 400px;
                }
                @media only screen and (min-width: 1700px) {
                    max-width: 400px;
                }
            `,
        },
    },
    Tooltiptext: {
        css: css`
            font-size: 12px !important;
        `,
    },
    iconProps: {
        css: css`
            _width: 5px;
        `,
    },
    input1: {
        css: css`S
          border:1px solid red;  
        `,
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            inputSelect: css`
                border-radius: 3px;
                font-size: 12px;
                width: 100%;
                float: right;
                height: 34px;
                padding: 9px 40px 9px 15px;
                font-weight: 600;
                border: 1px solid ${ADIColors.gray3} !important;

                :focus-within {
                    padding: 9px 40px 9px 15px;
                    outline-color: red;
                    outline-style: none;
                    outline-width: 0px;
                }
                &:focus {
                    ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                    outline-color: red;
                                    outline-style: none;
                                    outline-width: 0px;
                                `,
                        ],
                        "min",
                    );
                }}
                }
                &:focus-visible {
                    ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                    outline-color: ${ADIColors.darkBlack3};
                                    outline-style: ${getProp("theme.focus.style", "solid")};
                                    outline-width: 1px;
                                    outline-offset: 1px;
                                `,
                        ],
                        "min",
                    );
                }}
                }
                ::placeholder {
                    color: ${ADIColors.gray3};
                    opacity: 1;
                }

                ::-ms-input-placeholder {
                    color: ${ADIColors.gray3};
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            max-width: 300px;
                        `,
                        css`
                            max-width: 300px;
                        `,
                        css`
                            max-width: 400px;
                        `,
                    ])}
            `,
            formInputWrapper: css`
                display: flex;
                flex-direction: column !important;
                > button {
                    width: 35px !important;
                    height: 35px !important;
                    padding: 4px !important;
                    ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                                right: 0px !important;
                            `,
                        css`
                                right: 0px !important;
                            `,
                        null,
                    ])}
                    > ${IconWrapper} {
                        top: 0px;
                        right: 0px;
                        width: 100%;
                        height: 100%;
                        padding: 4px;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            max-width: 338.63px;
                        `,
                    ])}
                @media only screen and (max-width: 1700px) {
                    max-width: 400px;
                }
                @media only screen and (min-width: 1700px) {
                    max-width: 400px;
                }
            `,
        },
    },
    paymentMethodWrapper: {
        css: css`
            padding: 21px 25px 0 23px !important;
            border-radius: 4px;
            border: solid 1px ${getColor("common.border")} !important;
            background-color: ${getColor("common.background")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 21px 15px 0 15px !important;
                    `,
                    css`
                        padding: 21px 15px 0 15px !important;
                    `,
                    null,
                    css`
                        overflow-y: hidden;
                        overflow-x: hidden;
                        &::-webkit-scrollbar {
                            width: 4px;
                            overflow-y: auto;
                        }
                        &::-webkit-scrollbar-track {
                            background: ${getColor("common.background")};
                            overflow-y: auto;
                        }
                        &::-webkit-scrollbar-thumb {
                            background-color: ${ADIColors.lightGrey};
                            border-radius: 7px;
                            overflow-y: auto;
                        }
                    `,
                    css`
                        overflow-y: hidden;
                        overflow-x: hidden;
                        &::-webkit-scrollbar {
                            width: 4px;
                            overflow-y: auto;
                        }
                        &::-webkit-scrollbar-track {
                            background: ${getColor("common.background")};
                            overflow-y: auto;
                        }
                        &::-webkit-scrollbar-thumb {
                            background-color: ${ADIColors.lightGrey};
                            border-radius: 7px;
                            overflow-y: auto;
                        }
                    `,
                ])}
        `,
    },
    form: {
        css: css`
            > fieldset {
                > div {
                    max-width: 100%;
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                                width: 100% !important;
                            `,
                    css`
                                width: 100% !important;
                            `,
                    css`
                                width: 319px !important;
                            `,
                    null,
                    null,
                ])}
                }
            }
        `,
    },

    // P21 Change
    TooltipBody: {
        css: css`
            color: ${ADIColors.darkBlack3};
            font-weight: 400;
            font-size: 14px;
            width: auto;
        `,
    },
    zIndex: {
        css: css`
            z-index: 3;
        `,
    },
    zIndex2: {
        css: css`
            z-index: 2;
        `,
    },
    svgIcons: {
        css: css`
            background-color: red;
            color: white;
            width: 20px;
            height: 20px;
        `,
    },

    adipaymentDtoContainer: {
        css: css`
            width: 60%; // In Spire prod width is set ot 50%
            input:not(.react-datetime-picker__inputGroup__input):focus,
            select:not(.react-datetime-picker__inputGroup__input):focus,
            textarea:focus,
            .mobiusFileUpload:focus,
            .react-datetime-picker:focus {
                outline-style: none !important;
            }
            input:not(.react-datetime-picker__inputGroup__input),
            select:not(.react-datetime-picker__inputGroup__input),
            textarea,
            .mobiusFileUpload,
            .react-datetime-picker {
                border: 1px solid ${ADIColors.gray2} !important;
            }
            input:not(.react-datetime-picker__inputGroup__input):focus,
            select:not(.react-datetime-picker__inputGroup__input):focus,
            textarea:focus,
            .mobiusFileUpload:focus,
            .react-datetime-picker:focus,
            input:not(.react-datetime-picker__inputGroup__input) .focus-within,
            select:not(.react-datetime-picker__inputGroup__input) .focus-within,
            textarea .focus-within,
            .mobiusFileUpload .focus-within,
            .react-datetime-picker .focus-within {
                padding: 9.5px 15px !important;
            }
            margin-left: 0px;
            margin-right: auto;
        `,
    },
    fieldset: {
        css: css`
            margin: 0;
            padding: 0;
            border: 0;
        `,
    },
    heading: {
        css: css`
            font-size: 22px;
            font-weight: bold;
            line-height: normal;
            color: ${ADIColors.darkBlue1};
            letter-spacing: normal;
            text-align: left;
            padding: 0;
            margin: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 20px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    subHeading: {
        css: css`
            margin: 15px 0 18px;
            font-size: 16px;
            color: ${ADIColors.darkBlack3};
        `,
    },
    primaryButton: {
        css: css`
            border-radius: 4px;
            width: 130px;
            min-width: 130px !important;
            padding: 6px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: calc(50% - 6.5px);
                            min-width: calc(50% - 6.5px) !important;
                            margin: 0 0 0 6.5px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    secondaryButton: {
        css: css`
            border-radius: 4px;
            width: 130px;
            min-width: 130px !important;
            padding: 6px;
            border: 1px solid ${ADIColors.blue1};
            &:hover {
                background-color: ${ADIColors.secondaryButtonHoverLightBlue};
                color: ${ADIColors.blue1}!important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: calc(50% - 6.5px);
                            // min-width: calc(50% - 6.5px) !important;
                            margin: 0 6.5px 0 0;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    billAndPayStyledWrapper: {
        css: css`
            margin-top: 21px;
        `,
    },

    billAndPayStyledInnerWrapper: {
        css: css`
            justify-content: space-between;
        `,
    },
    billAndPayRadioButton: {
        css: css`
            ${TypographyStyle} {
                font-size: ${getProp("theme.typography.headerSecondary.size")};
                font-weight: 700;
            }
        `,
    },
    mainPaymentGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: end;
            gap: 10px;
            padding-right: 0;
            flex-wrap: wrap;
            flex-direction: row;
            justify-content: space-between;
            gap: 10px 0;
            > ${TypographyStyle} {
                width: 70%;
                flex-basis: 70%;
                display: flex;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-right: 15px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    cardDisplyWithButton: {
        css: css`
            display: flex;
            justify-content: space-between;
            gap: 10px;
            width: auto;
            flex-basis: auto;
            justify-content: flex-end;
            align-self: auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    null,
                    css`
                        align-self: self-start;
                        width: calc(100% - 52px);
                        width: auto;
                        flex-basis: auto;
                        justify-content: flex-end;
                        align-self: auto;
                    `,
                ])}
        `,
    },
    selectNewButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            gap: 10px;
            align-self: flex-start;
            margin: auto 0 0 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        max-width: 100%;
                        gap: 0px;
                    `,
                    css`
                        width: 100%;
                        max-width: 100%;
                        gap: 0px;
                    `,
                    null,
                    null,
                    css`
                        align-self: auto;
                        margin: auto 0 0 0;
                    `,
                ])}
        `,
    },

    rightLinks: {
        css: css`
            padding: 0 8px 0 0;
            font-size: ${getProp("theme.typography.headerSecondary.size")};
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            cursor: pointer;
            :hover {
                text-decoration: underline ${getColor("primary")};
            }
        `,
    },
    lTLPopupModal: {
        headlineTypographyProps: {
            variant: "h2",
            css: css`
                font-size: 34px;
                font-weight: 900;
                font-stretch: normal;
                font-style: normal;
                line-height: 36px;
                letter-spacing: normal;
                color: ${ADIColors.darkBlue1};
                border: 0;
                margin-top: 0;
                margin-bottom: 10px;
                padding-bottom: 0;
            `,
        },

        closeButtonProps: {
            css: css`
                padding: 0;
                margin-top: -30px;
                margin-right: -15px;
                &:hover {
                    background: transparent;
                    border-color: transparent;
                }
                > ${IconWrapper} {
                    color: ${getColor("primary")};
                    width: 35px;
                    height: 35px;
                    > svg {
                        width: 35px;
                        height: 35px;
                        stroke-width: 1.4px;
                    }
                }
            `,
        },
        cssOverrides: {
            headlineTypography: css`
                font-size: 28px;
                line-height: normal;
                margin-bottom: 0;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                font-size: 24px;
                                line-height: 1.41;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            `,
            modalBody: css`
                padding: 32px 28px;
                border-radius: 7px;
                overflow: hidden;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            max-width: calc(100% - 20px);
                            padding: 24px 20px;
                            top: 13%;
                            height: auto;
                            position: absolute;
                        `,
                        css`
                            top: 13%;
                            max-width: calc(100% - 20px);
                            border-radius: 6px;
                            padding: 24px 20px;
                            height: auto;
                            position: absolute;
                        `,
                        css`
                            max-width: 90%;
                            top: 8%;
                            height: auto;
                            position: absolute;
                        `,
                        css`
                            max-width: 90%;
                            top: 13%;
                            height: auto;
                            position: absolute;
                        `,
                        css`
                            max-width: 802px;
                            top: 15%;
                            margin: 0 auto;
                            height: auto;
                            position: absolute;
                        `,
                    ]);
                }}
                ${GridWrapper} {
                    max-width: 100%;
                }
            `,

            modalContent: css`
                padding: 0px;
                overflow: hidden auto;
                &::-webkit-scrollbar {
                    width: 8px;
                }

                &::-webkit-scrollbar-track {
                    background: ${getColor("common.accent")};
                }

                &::-webkit-scrollbar-thumb {
                    background-color: ${ADIColors.lightblue2};
                    border-radius: 7px;
                }
            `,
            modalTitle: css`
                border: unset;
                padding: 0px 10px 0px 0px;
            `,
        },
    },
    lTlContent: {
        css: css`
            margin: 16px 0 40px;
            line-height: normal;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin: 20px 0;
                            font-size: 14px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    lTlBtnContainer: {
        css: css`
            text-align: right;
            display: flex;
            justify-content: flex-end;
            button {
                margin-left: 23px;
                border-radius: 4px;
                padding: 12px 10px;
                outline: 0;
                :hover,
                :focus,
                :active {
                    outline: 0;
                }
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                                margin-right: 13px;
                                min-width: calc(50% - 13px);
                                width: calc(50% - 13px);
                                margin-left: 0;
                                &:last-child {
                                    margin-left: 13px;
                                    min-width: calc(50% - 13px);
                                    width: calc(50% - 13px);
                                    margin-right: 0;
                                }
                            `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            }
        `,
    },
    returnBtn: {
        css: css`
            min-width: 160px;
            span {
                color: ${ADIColors.blue1};
            }
            background-color: ${ADIColors.white};
            border-color: ${ADIColors.blue1};
            :hover {
                background-color: transparent;
            }
        `,
    },
    placeOrderBtn: {
        css: css`
            min-width: 222px;
        `,
    },

    modal: {
        size: 930,
        cssOverrides: {
            modalBody: css`
                border-radius: 6px;
                overflow-y: visible;
                max-height: calc(80vh - 140px);
                overflow-x: hidden;
                // height: auto;
                overflow-y: auto;
                height: unset;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            max-width: calc(100% - 20px);
                            // min-height: 90vh;
                        `,
                        css`
                            max-width: calc(100% - 20px);
                            // min-height: 90vh;
                        `,
                        css`
                            max-width: 70%;
                            height: unset;
                        `,
                        null,
                        null,
                    ])}
            `,
            modalTitle: css`
                display: none;
                padding: 0;
            `,
            modalContent: css`
                padding: 40px 20px;
                overflow-y: auto;
                overflow-x: hidden;
                section {
                    margin-bottom: 0px;
                }
                &::-webkit-scrollbar {
                    width: 8px;
                    overflow-y: auto;
                }
                &::-webkit-scrollbar-track {
                    background: ${getColor("common.background")};
                    overflow-y: auto;
                }
                &::-webkit-scrollbar-thumb {
                    background-color: ${ADIColors.lightGrey};
                    border-radius: 7px;
                    overflow-y: auto;
                }
                .modalContainer {
                    display: flex;
                }
                .modalText {
                    padding-left: 10px;
                }
            `,
        },
    },
};

const styles = checkoutReviewAndSubmitPaymentDetailsStyles;
const StyledForm = getStyledWrapper("form");
const StyledFieldSet = getStyledWrapper("fieldset");

const ADICheckoutReviewAndSubmitPaymentDetails = ({
    cartState,
    billToState,
    orderConfirmationPageLink,
    adiPaymentCustomizationDto,
    placeOrder,
    shipToState,
    toaster,
    history,
    loadCurrentCart,
    loadCart,
    checkoutCart,
    initialPaymentMethod,
    localCart,
    alternateShipAddress,
    billAction,
    deliveryMethodInProgress,
    updatePaymentMethod,
    updatedDeliveryMethod,
    deliveryMethod,
    setUpdateCartLoading,
    oneHourPickup,
    // Below props are being used by P21
    TaxAndShippingDtls,
    adiGetCreditCardListRecap,
    creditCardPaymentTypes,
    deliveryMethods, // using this methods from Store to make change in p21Frieghtcode as per the backend change done by Bhima.
}: Props) => {
    const errorMessage = "This field is required";
    const [poNumber, setPONumber] = useState("");
    const [poReference, setPoReference] = useState("");
    const [poRequestor, setPoRequestor] = useState("");
    const [poTown, setPoTown] = useState("");
    const [poJob, setPoJob] = useState("");
    const [poBranch, setPoBranch] = useState("");
    const [poAccount, setPoAccount] = useState("");
    const [error, setError] = useState(false);
    const [ccError, setCCError] = useState(false);
    const [isEmpty, setIsempty] = useState<any>(false); // P21 change
    const [syntaxVAlidation, isSyntaxVAlidation] = useState<any>(); // P21 Change
    const [errorRequestor, setErrorRequestor] = useState(false);
    const [errorReference, setErrorReference] = useState(false);
    const [displayLTLModal, setDisplayLTLModal] = useState(false);
    const [errorPoMessage, setErrorPoMessage] = useState("");
    const [errorRequestorMesssage, setErrorRequestorMessage] = useState("");
    const [errorReferenceMessage, setErrorReferenceMessage] = useState("");
    const [isApiErrorMessage, setIsApiErrorMessage] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [customeError, setcustomError] = useState<any>(true); // P21 change

    const [paymentMethod, setPaymentMethod] = useState("Bill");
    const [isCheckoutCartInit, setIsCheckoutCartInit] = useState(true);

    const [isCODCustomer, setIsCODCustomer] = useState(true);
    const [isNonCOD, setIsNonCOD] = useState<boolean | undefined>();
    const [isCODpickUp, setIsCODpickUp] = useState<boolean | undefined>();
    const [firstTimeRender, setFirstTimeRender] = useState(true);
    const [natAccount, setNatAccount] = useState("");
    const [custNumber, setCustNumber] = useState("");
    const [hideBillOrderDiv, setHideBillOrderDiv] = useState(true);
    const [isAdonisDown, setIsAdonisDown] = useState(false);

    const [inputBorde, setInputborder] = useState(false ? styles.input : styles.input1); // This is being used by P21
    const [inputBordeRef, setInputborderRef] = useState(false ? styles.input : styles.input1);
    const [IsRemoved, setIsRemoved] = useState(true); // This is being used by P21
    const [leftSideDivHeight, setLeftSideDivHeight] = React.useState<number>(0);

    const { value: cart } = cartState;
    const isQuote = cartState.value?.type == "Quote";

    //code for calculating the height and used in summary component
    const targetRef = React.useRef<HTMLDivElement>(null);
    let lastHeight = 0;
    React.useEffect(() => {
        const intervalId = setInterval(() => {
            if (targetRef.current) {
                const height = targetRef.current.offsetHeight + 25;
                const rightSideSavedHeight = sessionStorage.getItem("rightSideInnerDivHeight");
                if (height !== lastHeight && height <= Number(rightSideSavedHeight)) {
                    sessionStorage.setItem("leftSideInnerDivHeight", String(height));
                    sessionStorage.setItem("rightSideInnerDivHeight", String(height));
                    setLeftSideDivHeight(height);
                    lastHeight = height;
                } else {
                    setLeftSideDivHeight(Number(rightSideSavedHeight));
                    sessionStorage.setItem("leftSideInnerDivHeight", String(height));
                    sessionStorage.setItem("rightSideInnerDivHeight", String(height));
                }
            }
        }, 200);
        return () => clearInterval(intervalId);
    }, [targetRef]);

    useEffect(() => {
        //getApi(); // This is done by P21
        adiGetCreditCardListRecap({}); // P21 change
        setPaymentMethod("Bill");
        updatePaymentMethod({
            paymentMethod: "Bill",
        });
    }, []);

    useEffect(() => {
        if (firstTimeRender && cartState.value) {
            setFirstTimeRender(false);
            setHideBillOrderDiv(isCodCustomer(cartState.value));
        }
    }, [cartState.value]);

    useEffect(() => {
        if (cartState.value) {
            if (
                // Is COD and is ship.
                isCodCustomer(cartState.value) &&
                (updatedDeliveryMethod === "primary" || updatedDeliveryMethod === "alternate")
            ) {
                setHideBillOrderDiv(true);
                setPaymentMethod("CC");
                updatePaymentMethod({
                    paymentMethod: "CC",
                });
            } else if (
                // Is COD and is pickup in branch and locker.
                isCodCustomer(cartState.value) &&
                updatedDeliveryMethod === "pickup" &&
                billAction === "hideBill" &&
                oneHourPickup !== "isOneHourPickUp"
            ) {
                setPaymentMethod("CC");
                updatePaymentMethod({
                    paymentMethod: "CC",
                });
                setHideBillOrderDiv(true);
            } else if (
                // Is COD and is pickup in branch and OneHourPickup.
                isCodCustomer(cartState.value) &&
                updatedDeliveryMethod === "pickup" &&
                billAction === "showBill" &&
                oneHourPickup === "isOneHourPickUp"
            ) {
                setHideBillOrderDiv(false);
                updatePaymentMethod({
                    paymentMethod: "CC",
                });
                setPaymentMethod("CC");
            } else if (
                isCodCustomer(cartState.value) &&
                updatedDeliveryMethod === "pickup" &&
                billAction === "showBill"
            ) {
                setHideBillOrderDiv(false);
                updatePaymentMethod({
                    paymentMethod: "CC",
                });
                setPaymentMethod("CC");
            } else {
                setHideBillOrderDiv(false);
            }
        }
    }, [updatedDeliveryMethod, oneHourPickup]);

    // ADINA-52685 fix when we change the payment method, it was calling the update cart and load cart,
    // since cart is in dependency it will reset the saved card so i am removeing this logic,if anything breaking
    //  beacause of this change we need to handle in diffrent way
    // useEffect(() => {
    //     if (
    //         (cart?.properties?.shippingSelection === "primary" && !isCustomerCOD()) ||
    //         (cart?.properties?.shippingSelection === "alternate" && !isCustomerCOD()) ||
    //         (cart?.properties?.shippingSelection === "pickup" && paymentMethod === "Bill")
    //     ) {
    //         setSelectedSavedCardId("");
    //         setSelectedSavedCard(null);
    //     }
    // }, [cart?.properties]);

    useEffect(() => {
        if (cartState.value && isCheckoutCartInit) {
            const cartClone = cloneDeep(cartState.value);
            if (
                cartClone.properties["custCOD"] == "500" ||
                cartClone.properties["custCOD"] == "881" ||
                cartClone.properties["custCOD"] == "888" ||
                cartClone.properties["custCOD"] == "991" ||
                cartClone.properties["custCOD"] == "999"
            ) {
                cartClone.properties["CODCustomer"] = "Yes";
                if (cartClone.properties["shippingSelection"] == "pickup" || cartClone.properties["pickup_mob"]) {
                    cartClone.properties["isCODCustomer"] = "Y";
                    cartClone.properties["COD_pickup"] = "Y";
                } else {
                    cartClone.properties["isCODCustomer"] = "N";
                    cartClone.properties["COD_pickup"] = "N";
                }
            } else {
                cartClone.properties["isCODCustomer"] = "NonCod";
                cartClone.properties["CODCustomer"] = "No";
            }
            checkoutCart({
                cart: cartClone as CartModel as any,
                onSuccess() { },
            });
            // Below code commented in P21 change
            // setIsCheckoutCartInit(false);
            // setPoRequestor(cartState.value.adiPaymentCustomizationDto?.requestor.value);
            // setNatAccount(cartState.value.adiPaymentCustomizationDto?.natAccount);
            setCustNumber(cartState.value.adiPaymentCustomizationDto?.custNumber);
        }
    }, [cartState.value, isCheckoutCartInit]);

    // code added to bind the value for PO number //
    useEffect(() => {
        if (cartState.value?.type === "Quote") {
            setPONumber(cartState.value.properties.poNumber);
            setPoReference(cartState.value.properties.reference);
            setPoJob(cartState.value.properties.jobName); // P21 change
            setPoRequestor(cartState.value?.adiPaymentCustomizationDto?.requestor?.value);
        } else if (cartState.value?.type == "Order") {
            setPONumber("");
            setPoReference("");
            setPoJob(""); // P21 change
        }
    }, [
        cartState.value?.properties?.poNumber,
        cartState.value?.properties?.reference,
        cartState.value?.properties?.job,
    ]); // job property is being used by P21

    // *******************************//
    useEffect(() => {
        if (initialPaymentMethod && !paymentMethod) {
            setPaymentMethod(initialPaymentMethod);
        }
    }, [initialPaymentMethod]);

    // This code is commented by P21
    // const updatePaymentMethodForSelectedCust = (delivery: string) => {
    //     const updatedPaymentMethod = delivery;
    //     if (cartState.value) {
    //         const cartClone = JSON.parse(JSON.stringify(cartState.value));
    //         const payBill = updatedPaymentMethod === "Bill" ? true : false;
    //         cartClone!.properties["paymentMethod"] = payBill ? "bill" : "CC";

    //         cartClone!.paymentMethod = {
    //             ...cartClone!.paymentMethod,
    //             isCreditCard: payBill ? false : true,
    //             description: payBill ? "Bill" : "Credit Card",
    //             isPaymentProfile: false,
    //             name: payBill ? "BILL" : "CC",
    //         };
    //         updateCart({
    //             cart: {
    //                 ...cartClone,
    //             },
    //         }).then(() => {
    //             loadCart({
    //                 cartId: cartClone?.id || "current",
    //                 loading: false,
    //                 showLoad: false,
    //             } as LoadCartApiInterface);
    //         });
    //     }
    // };

    useEffect(() => {
        if (billAction && !deliveryMethodInProgress) {
            if (billAction === "showBill") {
                setHideBillOrderDiv(false);
                setPaymentMethod(isCodCustomer(cartState.value!) ? "CC" : "Bill");

                if (!isCodCustomer(cartState.value!)) {
                    clearSelectedSavedCard();
                }
            } else {
                setHideBillOrderDiv(true);
                setPaymentMethod("CC");
                // updatePaymentMethodForSelectedCust("CC"); // Code is commented by P21
            }
        }
    }, [billAction, deliveryMethodInProgress]);

    useEffect(() => {
        if (localCart && localCart.properties?.isCODCustomer) {
            if (localCart.properties?.isCODCustomer === "Y" || localCart.properties?.isCODCustomer === "Yes") {
                setIsCODCustomer(true);
                setIsNonCOD(false);
            } else if (localCart.properties?.isCODCustomer === "NonCod") {
                setIsCODCustomer(true);
                setIsNonCOD(true);
            } else {
                setIsCODCustomer(false);
                setIsNonCOD(false);
            }
        }
    }, [localCart]);

    useEffect(() => {
        if (localCart && localCart.properties?.COD_pickup) {
            let cartClone = cloneDeep(localCart);
            let temp = cartClone.properties["COD_pickup"];
            if (cartClone) {
                if (!isNonCOD) {
                    if (temp == "Y" && billAction !== "hideBill" && updatedDeliveryMethod === "pickup") {
                        if (cartClone.properties["shippingSelection"] === "pickup") {
                            setIsCODpickUp(true);
                            setPaymentMethod("Bill");
                            onChangePaymentMethodHandler("Bill");
                        }
                    } else {
                        setIsCODpickUp(false);
                        setPaymentMethod("CC");
                    }
                } else if (cartClone.ccRequired) {
                    setPaymentMethod("CC");
                } else {
                    setPaymentMethod("Bill");
                }
            } else if (!isNonCOD) {
                if (temp == "Y") {
                    setIsCODpickUp(true);
                } else {
                    setIsCODpickUp(false);
                }
            }
        }
    }, [localCart, isNonCOD]);

    const validatePONumber = (name: string) => {
        setErrorPoMessage("");
        const error =
            !name && adiPaymentCustomizationDto.po.isRequired && !adiPaymentCustomizationDto.po.isReadonly
                ? "A valid Purchase Order number is required."
                : "";
        setErrorMsg(error);
        if (error) {
            if (!name) {
                setErrorPoMessage(
                    adiPaymentCustomizationDto.po.messages.find((item: any) => item.title == "required").message,
                );
                return false;
            }
        }
        // StC PO
        const validateStcPO = () => {
            var p = /^([Tt]{1}[Rr]{1})(\d{8})$/,
                is10 = name.length == 10;
            switch (custNumber) {
                case "V5753":
                    p = /^([Tt]{1}[Rr]{1})(\d{8})$/;
                    break;
                case "BV960":
                    p = /^([Tt]{1}[Rr]{1}[Cc]{1})(\d{7})$/;
                    break;
                case "BW474":
                    p = /^([Cc]{1})(\d{10})$/;
                    is10 = name.length == 11;
                    break;
            }
            var cn = ["V5753", "BV960", "BW474"],
                isn = p.test(name),
                ina = cn.includes(custNumber);
            if (ina) {
                if (!(is10 && isn)) {
                    setErrorPoMessage(
                        adiPaymentCustomizationDto.po.messages.find((item: any) => item.title == "po").message,
                    );
                }
                return is10 && isn;
            } else {
                return true;
            }
        };

        // STN
        const validateStnPO = () => {
            var valid = true;
            var cn = ["L6130", "AB227", "N5603", "AE742"],
                p = /^\d+$/,
                isn = p.test(name),
                is10 = name.length == 10,
                ina = cn.includes(custNumber);
            if (ina) {
                if (!(is10 && isn)) {
                    setErrorPoMessage(
                        adiPaymentCustomizationDto.po.messages.find((item: any) => item.title == "po").message,
                    );
                } else {
                    setErrorPoMessage("");
                }
                return is10 && isn;
            }

            var cnStn = ["L6129", "AB212"],
                p = /^\d+$/,
                isn = p.test(name),
                inaStn = cnStn.includes(custNumber);

            if (inaStn) {
                if (name.indexOf(".") != -1 || isNaN(Number(name)) || name.toString().length !== 7 || /\s/.test(name)) {
                    setErrorPoMessage(
                        adiPaymentCustomizationDto.po.messages.find((item: any) => item.title == "po").message,
                    );

                    return (valid = false);
                } else {
                    setErrorPoMessage("");
                }
            }
            return valid;
        };

        // PRO
        const validateProPO = () => {
            var p = /\b\d{7}\b/;
            if (!p.test(name)) {
                setErrorPoMessage(
                    adiPaymentCustomizationDto.po.messages.find((item: any) => item.title == "po").message,
                );
            }
            return p.test(name);
        };

        // JCL
        const validateJclPO = () => {
            return name.length != 0;
        };

        let _out;
        switch (natAccount) {
            case "ADT":
                _out = name.length > 0;
                break;
            case "STC":
                _out = validateStcPO();
                break;
            case "STN":
                _out = validateStnPO();
                break;
            case "PRO":
                _out = validateProPO();
                break;
            case "JCL":
                _out = validateJclPO();
                break;
            default:
                if (cartState.value?.adiPaymentCustomizationDto.po.isRequired && name.length == 0) {
                    _out = false;
                } else {
                    _out = true;
                }
        }

        return _out;
    };

    // Nam validation
    const validateReferenceNumber = (name: string) => {
        const error =
            !name && adiPaymentCustomizationDto.reference.isRequired && !adiPaymentCustomizationDto.reference.isReadonly
                ? errorMessage
                : "";
        setErrorMsg(error);
        if (error) {
            if (!name) {
                setErrorReferenceMessage(
                    adiPaymentCustomizationDto.reference.messages.find((item: any) => item.title == "required").message,
                );
                return false;
            }
        }

        const validateStnRN = () => {
            setErrorReferenceMessage("");
            var cn = ["L6129", "L6130", "AB212", "AB227"],
                ina = cn.includes(custNumber);
            if (name.length < 9) {
                setErrorReferenceMessage(
                    adiPaymentCustomizationDto.reference.messages.find((item: any) => item.title == "reference")
                        .message,
                );
                return false;
            }
            var p = /^\d+$/,
                woNum = name.substring(0, 7),
                hasWO = p.test(woNum),
                has8Space = name.substring(7, 8) == " ",
                hasJob = name.substring(8).length > 0 && name.substring(8, 9) != " ";
            if (ina && !(hasWO && has8Space && hasJob)) {
                (woNum = name.substring(0, 8)),
                    (hasWO = p.test(woNum)),
                    (has8Space = name.substring(8, 9) == " "),
                    (hasJob = name.substring(9).length > 0 && name.substring(9, 10) != " ");
            }
            if (!(hasWO && has8Space && hasJob)) {
                setErrorReferenceMessage(
                    adiPaymentCustomizationDto.reference.messages.find((item: any) => item.title == "reference")
                        .message,
                );
                return false;
            } else {
                return true;
            }
        };

        let _out = true;
        switch (natAccount) {
            case "STN":
                _out = validateStnRN();
                break;
        }

        return _out;
    };

    const validateRequestorNumber = (name: string) => {
        if (adiPaymentCustomizationDto.requestor !== null) {
            const error =
                !name &&
                    adiPaymentCustomizationDto.requestor.isRequired &&
                    !adiPaymentCustomizationDto.requestor.isReadonly
                    ? errorMessage
                    : "";
            setErrorMsg(error);

            return !error;
        }
        return true;
    };
    function parsePoSyntax(po: string) {
        if (po === "") {
            return [];
        }

        let poArray = [],
            poArrayObj = {},
            lastChar = "",
            sameCharCnt = 0;

        for (let i = 0; i < po.length; i++) {
            let thisChar = po[i];
            let poArrayPntr: any = poArray.length - 1;
            sameCharCnt = thisChar === lastChar ? ++sameCharCnt : 1;

            switch (thisChar) {
                case "@":
                case "#":
                    if (sameCharCnt !== 1) {
                        poArray[poArrayPntr] = { c: thisChar, n: sameCharCnt };
                    } else {
                        poArray.push({ c: thisChar, n: sameCharCnt });
                    }
                    break;
                default:
                    poArray.push({ c: thisChar, n: sameCharCnt });
                    break;
            }
            lastChar = po[i];
        }
        return poArray;
    }

    // Get example format
    const getFormatExample = (POSyntax: string) => {
        const ary = parsePoSyntax(POSyntax);
        let tempLblExmpl = "";
        const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const numbers = "1234567890";
        const ln = letters.concat(numbers);

        const getSecureRandom = () => {
            const array = new Uint32Array(1);
            crypto.getRandomValues(array);
            return array[0] / (0xffffffff + 1);
        };

        const mx = ln
            .split("")
            .sort(() => getSecureRandom() - 0.5)
            .join("");

        for (const itm of ary) {
            switch (itm.c) {
                case "@":
                    tempLblExmpl += letters.substring(0, itm.n);
                    break;
                case "#":
                    tempLblExmpl += numbers.substring(0, itm.n);
                    break;
                case "*":
                    tempLblExmpl += mx.substring(0, itm.n);
                    break;
                case "^":
                    let min = 1,
                        max = 4,
                        cnt = Math.floor(getSecureRandom() * (max - min + 1)) + min;
                    tempLblExmpl += mx.substring(0, cnt);
                    break;
                default:
                    tempLblExmpl += itm.c;
                    break;
            }
        }

        return tempLblExmpl;
    };

    // Convert POSyntax to regex string
    function poSyntaxToRegexStr(po: string) {
        if (po === "") {
            return "^.*$"; // Allow any value if poSyntax is empty
        }

        const sa = parsePoSyntax(po);
        let rx = "^";

        // Build regex
        for (let i = 0; i < sa.length; i++) {
            let o = sa[i],
                n = o.n,
                c = o.c,
                v = "";

            switch (c) {
                case " ":
                    rx += "\\s";
                    break;
                case "@":
                    rx += "[a-zA-Z]";
                    break;
                case "#":
                    rx += "\\d";
                    break;
                case "*":
                    rx += "[a-zA-Z0-9]";
                    break;
                case "^":
                    rx += ".+";
                    break;
                default:
                    rx += "[" + c + "]";
                    break;
            }
            rx += n > 1 ? "{" + n + "}" : "";
        }
        rx += "$";
        return rx;
    }
    // Syntax Validation
    function isValid(poSyntax: string, poValue: string) {
        let rs = poSyntaxToRegexStr(poSyntax),
            rx = new RegExp(rs);

        return rx.test(poValue);
    }
    // On Change PO
    const onChangePoHandler = (e: ChangeEvent<HTMLInputElement>) => {
        switch (e.currentTarget.name) {
            case "poNumber": {
                const inputValue: string = e.target.value;
                setPONumber(inputValue);
                if (adiPaymentCustomizationDto.po.isRequired && inputValue.trim() === "") {
                    setError(true);
                    setErrorMsg("This field is required");
                    isSyntaxVAlidation(
                        `${adiPaymentCustomizationDto.po.syntax} Example: ${getFormatExample(
                            adiPaymentCustomizationDto.po.syntax,
                        )}`,
                    );
                    return;
                }
                if (adiPaymentCustomizationDto.po.syntax) {
                    if (isValid(adiPaymentCustomizationDto.po.syntax, inputValue)) {
                        setError(false);
                        // eslint-disable-next-line no-constant-condition
                        setInputborder(false ? styles.input : styles.input1);
                        isSyntaxVAlidation("");
                    } else {
                        setError(true);
                        isSyntaxVAlidation(
                            `${adiPaymentCustomizationDto.po.syntax} Example: ${getFormatExample(
                                adiPaymentCustomizationDto.po.syntax,
                            )}`,
                        );
                        return;
                    }
                }

                if (adiPaymentCustomizationDto.po.isRequired) {
                    if (!validatePONumber(inputValue)) {
                        setError(true);
                        // isSyntaxVAlidation(
                        //     `${adiPaymentCustomizationDto.po.syntax} Example: ${getFormatExample(
                        //         adiPaymentCustomizationDto.po.syntax,
                        //     )}`,
                        // );

                        return;
                    }
                }

                break;
            }
            case "poReference": {
                setPoReference(e.currentTarget.value);

                if (poReference.length > 0) {
                    setErrorReference(false);
                    setInputborderRef(false ? styles.input : styles.input1);
                }
                if (adiPaymentCustomizationDto.reference.isRequired) {
                    if (!validateReferenceNumber(e.currentTarget.value)) {
                        setErrorReference(true);
                        setErrorMsg(errorMessage);
                        return;
                    }
                }
                break;
            }
            case "poJob": {
                setPoJob(e.currentTarget.value);
                break;
            }
        }
    };

    //On Change Payment Method
    const onChangePaymentMethodHandler = async (e: ChangeEvent<HTMLInputElement> | string) => {
        const updatedPaymentMethod = typeof e === "string" ? e : e.currentTarget.value;

        if (updatedPaymentMethod === "Bill") {
            setSelectedSavedCard(null);
            setSelectedSavedCardId("");
        }
        setPaymentMethod(updatedPaymentMethod);
        updatePaymentMethod({
            paymentMethod: updatedPaymentMethod,
        });

        // if (cartState.value) {
        //     const cartClone = JSON.parse(JSON.stringify(cartState.value));
        //     const payBill = updatedPaymentMethod === "Bill" ? true : false;
        //     cartClone!.properties["paymentMethod"] = payBill ? "bill" : "CC";

        //     cartClone!.paymentMethod = {
        //         ...cartClone!.paymentMethod,
        //         isCreditCard: payBill ? false : true,
        //         description: payBill ? "Bill" : "Credit Card",
        //         isPaymentProfile: false,
        //         name: payBill ? "BILL" : "CC",
        //     };
        //     setUpdateCartLoading({
        //         loading: true,
        //     });
        //     updateCart({
        //         cart: {
        //             ...cartClone,
        //         },
        //     }).then(() => {
        //         loadCart({
        //             cartId: cartClone?.id || "current",
        //             loading: false,
        //             showLoad: false,
        //         } as LoadCartApiInterface);
        //     });
        // }
    };

    const [savedCard, setSavedCard] = useState<GetSavedCreditCardsModel | null>(creditCardPaymentTypes);
    const [savedEditCard, setSavedEditCard] = useState<CreditCardDtoCollection | null>(null);
    const [selectedSavedCard, setSelectedSavedCard] = useState<CreditCardDtoCollection | null>(null);
    const [selectedSavedCardId, setSelectedSavedCardId] = useState("");

    const expirationMonths = cartState?.value && cartState.value?.paymentOptions?.expirationMonths;
    const expirationYears = cartState?.value && cartState.value?.paymentOptions?.expirationYears;

    const paymentOptions = cartState?.value ? cartState?.value.paymentOptions : undefined;

    const paymentMethods = paymentOptions ? paymentOptions.paymentMethods : undefined;

    const paymentMethodDto = paymentMethods?.find(method => method.name === paymentMethod);

    // New Card
    const [isNewCardOpen, setIsNewCardOpen] = useState(false);
    const [newCreditCartPopup, setnewCreditCartPopup] = useState<boolean>(false);

    const initialNewCardErrorState = {
        selectedCardType: false,
        creditCardNumber: false,
        expMonth: false,
        expYear: false,
        securityCode: false,
        zipCode: false,
    };

    const [formError, setFormError] = useState(initialNewCardErrorState);

    const [isFormError, setIsFromError] = useState(false);
    const [isApiError, setIsApiError] = useState(true);
    const [isReturnedFrom, setIsReturnedFrom] = useState("");
    const [newCardDetails, setNewCardDetails] = useState<CreditCardDtoCollection | null>(null);
    const [newCard, setNewCardFlag] = useState(false);

    const handleNewCardDetails = (data: any) => {
        setSelectedSavedCardId(data.ccid);
        let item: any = {
            id: data.ccid,
            cardNumber: data.ccNumber,
            cardType: data.ccType,
            expiryMonth: data.ccExpiryMonth,
            expiryYear: data.ccExpiryYear,
            cardCVC: data.ccCvc,
            cardToken: data.ccToken,
        };
        setNewCardFlag(true);
        setNewCardDetails(item);
        setSelectedSavedCard(item);
    };

    const closeNewCard = (val: boolean) => {
        if (val) {
            setPaymentMethod("CC");
        }
        setFormError(initialNewCardErrorState);
        setIsNewCardOpen(false);
        setIsFromError(false);
        setIsApiError(false);
        setnewCreditCartPopup(false);
    };
    const openNewCard = () => {
        setFormError(initialNewCardErrorState);
        setIsNewCardOpen(true);
        setIsFromError(false);
        setIsApiError(false);
    };
    const OpennewCreditCardPopup = () => {
        setnewCreditCartPopup(true);
        // closeNewCard(false)
    };

    // Get Card
    const [isGetCardOpen, setIsGetCardOpen] = useState(false);
    const closeGetCard = () => {
        setIsRemoved(true);
        setIsGetCardOpen(false);
    };
    const openGetCard = () => {
        setNewCardFlag(false);
        getApi();
        setIsGetCardOpen(true);
    };

    const getApi = async (page = 1, pageSize = 4, prevResult?: any) => {
        const result = (await getSavedCreditCards({
            page,
            pageSize,
            contains: prevResult?.ccToken || prevResult?.cardToken,
        })) as GetSavedCreditCardsModel;
        setSavedCard(result);
        if (result && result.creditCardDtoCollection.length === 0) {
            closeGetCard();
            return;
        }
        const prevCardToken = prevResult?.ccToken || prevResult?.cardToken;
        if (prevCardToken) {
            const selectedCardIdData = result.creditCardDtoCollection.find(item => item.id === prevCardToken);
            if (selectedCardIdData) {
                prevResult = selectedCardIdData;
            }
        }

        const hasContainsProperties = result.properties.contains != null && result.properties.hasContains !== null;
        if (
            hasContainsProperties &&
            result.properties.contains == (prevResult?.ccToken || prevResult?.cardToken) &&
            result.properties.hasContains == "True"
        ) {
            const card: CreditCardDtoCollection = {
                cardType: prevResult.ccType || prevResult.cardType,
                cardNumber: prevResult.ccNumber || prevResult.cardNumber,
                cardToken: prevResult.ccToken || prevResult.cardToken,
                cardCVC: prevResult.ccCvc || prevResult.cardCVC,
                postalCode: prevResult.ccPostalCode || prevResult.postalCode,
                expiryMonth: prevResult.ccExpiryMonth || prevResult.expiryMonth,
                expiryYear: prevResult.ccExpiryYear || prevResult.expiryYear,
                isDefault: prevResult.isDefault,
                id: prevResult.ccToken || prevResult.id,
                uri: "",
                properties: {},
                creditCardBankNumber: "",
                paymentMethodId: "",
                paymentTypeDesc: "",
                paymentTypeId: "",
                period: "",
                yearForPeriod: "",
            };

            if (isReturnedFrom == "fromSelectedCC" || isReturnedFrom === "fromGetCCModal") {
                setSelectedSavedCard(card);
            }
        } else {
            setIsApiErrorMessage(
                `${location.href.toLowerCase().indexOf("fr.") != -1
                } ? 'No se pudo agregar la tarjeta de cr�dito. Vuelva a intentarlo.' : 'Adding Credit Card failed, please try again.'`,
            );
        }
    };

    const onChangePage = (newPageIndex: number) => {
        getApi(newPageIndex);
    };

    const removeCard = async (
        // cardId: string,
        // type: string,
        // cardExpiryMonth: string,
        // cardExpiryYear: string,

        // id: string,,
        card: CreditCardDtoCollection,
        index: number,
    ) => {
        //  const cardToken = `${id}|${type}`;
        // const cardData: ADICreditCardsModelNA = card[index];
        const data = card;
        const deleteResponse = await removeCreditCardNANew({
            data,
            // cardToken,
        });
        if (deleteResponse != undefined && deleteResponse.result.properties["returnCode"] == "99") {
            setIsRemoved(false);
        } else {
            const selectedEditCard = savedCard?.creditCardDtoCollection[index];
            if (selectedEditCard) {
                setIsRemoved(true);
                setUpdateExpMonth("");
                setUpdateExpYear("");
                setSavedEditCard(null);
                setSelectedSavedCard(null);
                setSelectedSavedCardId("");
            }
        }

        getApi();
    };

    // Edit Card
    const [isEditCardOpen, setIsEditCardOpen] = useState(false);
    const closeEditCard = () => {
        setIsEditCardOpen(false);
        if (isReturnedFrom === "fromGetCCModal") {
            openGetCard();
        }
    };
    const openEditCard = () => {
        setIsEditCardOpen(true);
    };

    useEffect(() => {
        if (selectedSavedCard) {
            setSelectedSavedCardId(selectedSavedCard.id);
            setCCError(false);
        }
    }, [selectedSavedCard]);

    useEffect(() => {
        setSavedCard(creditCardPaymentTypes);
    }, [creditCardPaymentTypes]);

    const [updateExpYear, setUpdateExpYear] = useState("");
    const [updateExpMonth, setUpdateExpMonth] = useState("");

    const setEditCard = (editId: any, returnfrom: string, index: number) => {
        setSelectedSavedCardId(editId);
        let selectedEditCard: any = null;
        if (index !== 1000) {
            selectedEditCard = savedCard?.creditCardDtoCollection[index];
        } else {
            selectedEditCard = savedCard?.creditCardDtoCollection.find(
                (item: CreditCardDtoCollection) =>
                    item.id === editId &&
                    item.cardNumber == selectedSavedCard?.cardNumber &&
                    item.cardType == selectedSavedCard.cardType &&
                    item.expiryMonth == selectedSavedCard.expiryMonth &&
                    item.expiryYear == selectedSavedCard.expiryYear,
            );
        }
        if (newCardDetails) {
            setUpdateExpMonth(newCardDetails.expiryMonth);
            setUpdateExpYear(newCardDetails.expiryYear);
            setSavedEditCard(newCardDetails);
        }
        if (returnfrom === "fromSelectedCC") {
            setUpdateExpMonth(editId.expiryMonth);
            setUpdateExpYear(editId.expiryYear);
            setSavedEditCard(editId);
        }
        if (selectedEditCard) {
            setUpdateExpMonth(selectedEditCard.expiryMonth);
            setUpdateExpYear(selectedEditCard.expiryYear);
            setSavedEditCard(selectedEditCard);
        }
        openEditCard();
        closeGetCard();
        setIsReturnedFrom(returnfrom);
    };

    const checkExpirationDate = (month: any, year: any) => {
        var d = new Date();
        month = parseInt(month);
        year = parseInt(d.getFullYear().toString().substring(0, 2) + year);
        if (year == d.getFullYear() && month < d.getMonth() + 1) {
            return true;
        }

        if (year < d.getFullYear()) {
            return true;
        }

        return false;
    };

    const handleCardChange = (e: ChangeEvent<HTMLInputElement>) => {
        const cardParts = e.currentTarget.title.split("|");
        const cardId = cardParts[0] ?? "";
        const cardType = cardParts[1] ?? "";
        const cardExpiryMonth = cardParts[2] ?? "";
        const cardExpiryYear = cardParts[3] ?? "";

        const selectedCard = savedCard?.creditCardDtoCollection.filter(
            (item: CreditCardDtoCollection) =>
                item.id === cardId &&
                item.cardType === cardType &&
                item.expiryMonth === cardExpiryMonth &&
                item.expiryYear === cardExpiryYear,
        );

        if (selectedCard?.length) {
            const isExpired = checkExpirationDate(selectedCard[0].expiryMonth, selectedCard[0].expiryYear);
            if (isExpired) return;
            const extractedValue = e.currentTarget.value.split("|")[0];
            setSelectedSavedCardId(extractedValue);
            setSelectedSavedCard(selectedCard[0]);
            setPaymentMethod("CC");
            closeGetCard();
        }
    };

    const clearSelectedSavedCard = () => {
        setSelectedSavedCardId("");
        setSelectedSavedCard(null);
    };

    const closeLTLModal = () => {
        setDisplayLTLModal(false);
    };

    const placeOrderHandler = () => {
        setDisplayLTLModal(false);
        document.getElementById("reviewAndSubmitPaymentForm-submit")?.click();
    };

    const cardTypes = cart?.paymentOptions && cart.paymentOptions?.cardTypes;
    const ispaymentOptionsLoaded = cardTypes && cardTypes.length > 0 ? true : false;
    const filterCardType = (key: string) => {
        const result = cardTypes && cardTypes.filter((item: { key: string; value: string }) => item.key === key);
        //return result?.length ? result[0].value : key;
        if (result?.length) return result[0].value;
        else if (key.length > 1) return key;
        else return "";
    };

    if (!cart || cart.requiresApproval || !orderConfirmationPageLink) {
        return null;
    }
    if (cartState.value && cartState.value.paymentOptions?.isPayPal) {
        return null;
    }

    const validateFields = () => {
        let isFormValid = true;

        if (!validatePONumber(poNumber)) {
            setError(true);
            setErrorMsg(errorMessage);
            isFormValid = false;
        } else {
            setError(false); // Reset if previously errored
        }
        if (poNumber && adiPaymentCustomizationDto.po.syntax) {
            if (!isValid(adiPaymentCustomizationDto.po.syntax, poNumber)) {
                setError(true);
                isSyntaxVAlidation(
                    `${adiPaymentCustomizationDto.po.syntax} Example: ${getFormatExample(
                        adiPaymentCustomizationDto.po.syntax,
                    )}`,
                );
                isFormValid = false;
            }
        }

        if (!validateRequestorNumber(poRequestor)) {
            setErrorRequestor(true);
            setErrorMsg(errorMessage);
            isFormValid = false;
        } else {
            setErrorRequestor(false);
        }

        if (!validateReferenceNumber(poReference) && !isQuote) {
            setErrorReference(true);
            setErrorMsg(errorMessage);
            isFormValid = false;
        } else {
            setErrorReference(false);
        }

        if (paymentMethod === "CC" && !selectedSavedCardId) {
            setCCError(true);
            setErrorMsg("");
            isFormValid = false;
        } else {
            setCCError(false);
        }

        return isFormValid;
    };

    const isCodCustomerNew = (cart: any) => {
        return (
            cart.properties["custCOD"] == "500" ||
            cart.properties["custCOD"] == "881" ||
            cart.properties["custCOD"] == "888" ||
            cart.properties["custCOD"] == "991" ||
            cart.properties["custCOD"] == "999"
        );
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        // isSyntaxVAlidation(
        //     `${adiPaymentCustomizationDto.po.syntax} Example: ${getFormatExample(
        //         adiPaymentCustomizationDto.po.syntax,
        //     )}`,
        // );
        if (poReference.length == 0 && adiPaymentCustomizationDto.reference.isRequired == true) {
            setInputborderRef(true ? styles.input : styles.input1);
        }

        if (poNumber.length == 0 && adiPaymentCustomizationDto.po.isRequired) {
            setInputborder(true ? styles.input : styles.input1);
        }
        if (poReference.length == 0 && adiPaymentCustomizationDto.reference.isRequired == true) {
            setErrorReference(true);
            setErrorReferenceMessage(errorMessage);
        } else if (poReference.length > 0) {
            setErrorReference(false);
        } else {
            setErrorReference(false);
        }
        if (!validateFields()) {
            return;
        }
        if (cart.properties["deliveryCarrier"] === "LT" && !displayLTLModal) {
            setDisplayLTLModal(true);
            return;
        }

        let LTLLiftGateRequired = cart.properties.LTLLiftGateRequired;
        let LTLInSideDelivery = cart.properties.LTLInSideDelivery;
        let isLTL = "Y";

        if (deliveryMethod === "9008-0") {
            LTLLiftGateRequired = "N";
            LTLInSideDelivery = "N";
        } else if (deliveryMethod === "9008-1") {
            LTLLiftGateRequired = "Y";
            LTLInSideDelivery = "N";
        } else if (deliveryMethod == "9008-2") {
            LTLLiftGateRequired = "N";
            LTLInSideDelivery = "Y";
        } else if (deliveryMethod == "9008-3") {
            LTLLiftGateRequired = "Y";
            LTLInSideDelivery = "Y";
        } else {
            LTLLiftGateRequired = "";
            LTLInSideDelivery = "";
            isLTL = "";
        }

        // let updatedCartLineOject = cloneDeep(cart);
        let updatedCartLineOject = JSON.parse(JSON.stringify(cart!));
        if (updatedCartLineOject && paymentMethod == "CC" && updatedCartLineOject.paymentOptions) {
            if (updatedCartLineOject.paymentOptions.paymentMethods) {
                updatedCartLineOject?.paymentOptions?.paymentMethods.push({
                    name: "CC",
                    description: "Credit Card",
                    isCreditCard: true,
                    isECheck: false,
                    isPaymentProfile: false,
                    cardType: "null",
                    billingAddress: "null",
                    isPaymentProfileExpired: false,
                    tokenScheme: "null",
                });
            }
        } else {
            if (updatedCartLineOject?.paymentOptions?.paymentMethods) {
                updatedCartLineOject?.paymentOptions?.paymentMethods.push({
                    name: "BILL",
                    description: "Bill",
                    isCreditCard: false,
                    isECheck: false,
                    isPaymentProfile: false,
                    cardType: "null",
                    billingAddress: "null",
                    isPaymentProfileExpired: false,
                    tokenScheme: "null",
                });
            }
        }

        const selectedDeliveryMethodValue = deliveryMethods?.find(method => method.cd == deliveryMethod);

        if (deliveryMethods) {
            updatedCartLineOject.properties["p21FreightCode"] = selectedDeliveryMethodValue?.p21FreightCode; // updating the p21FreightCode from the value we are getting from methods.
        }

        updatedCartLineOject.properties["thirdPartyOnFileId"] =
            !updatedCartLineOject.properties["thirdPartyOnFileId"] || cart.properties["thirdPartyOnFileId"] == ""
                ? ""
                : cart.properties["thirdPartyOnFileId"];
        updatedCartLineOject.properties["p21BranchCode"] = updatedCartLineOject.p21BranchCode;
        updatedCartLineOject.properties["preferredSourceLocId"] = updatedCartLineOject.preferredSourceLocId;

        if (updatedCartLineOject.type.toLowerCase() == "quote") {
            if (updatedCartLineOject.poNumber.toLowerCase() != poNumber.toLowerCase())
                updatedCartLineOject.properties["isPONumberChanged"] = "true";
            else updatedCartLineOject.properties["isPONumberChanged"] = "false";
        }

        //for shiptoId

        let shipToId = "";
        if (cart.properties["shippingSelection"] === "alternate" && alternateShipAddress?.id) {
            shipToId = alternateShipAddress?.id;
            updatedCartLineOject.shipToId = shipToId;
        } else if (shipToState.value?.id && cart.properties["shippingSelection"] !== "alternate") {
            shipToId = shipToState.value?.id;
            updatedCartLineOject.shipToId = shipToId;
        }

        /*******************/
        if (localCart && localCart.carrier?.description === "Ship") {
            placeOrder({
                poNumber,
                reference: poReference,
                requestor: poRequestor,
                cart: updatedCartLineOject,
                properties: {
                    ...updatedCartLineOject.properties,
                    LTLLiftGateRequired: LTLLiftGateRequired,
                    LTLInSideDelivery: LTLInSideDelivery,
                    isLTL: isLTL,
                    reference: poReference,
                    requestor: poRequestor,
                    job: poJob,
                    paymentMethod: paymentMethod === "Bill" ? "BILL" : "CC",
                    // p21BranchCode: cart?.p21BranchCode,
                    preferredSourceLocId: cart?.preferredSourceLocId,
                    thirdPartyOnFileId: "",
                    // p21FreightCode: "PPA",
                    // isPONumberChanged: "false",
                },
                shipToId: shipToId,
                paymentMethod: paymentMethod === "Bill" ? "BILL" : "CC",
                saveCard: false,
                cardHolderName: "",
                selectedCCId: selectedSavedCard?.id || "",
                cardNumber: selectedSavedCard?.cardNumber || "",
                cardType: selectedSavedCard?.cardType || "",
                expirationMonth: Number(selectedSavedCard?.expiryMonth) || 0,
                expirationYear: Number(selectedSavedCard?.expiryYear) || 0,
                securityCode: selectedSavedCard?.cardCVC || "",
                useBillingAddress: cart.properties["shippingSelection"] === "alternate" ? false : true,
                address1:
                    cart.properties["shippingSelection"] === "alternate"
                        ? alternateShipAddress?.address1 || ""
                        : shipToState.value?.address1 || "",
                countryId:
                    cart.properties["shippingSelection"] === "alternate"
                        ? alternateShipAddress?.country?.id || ""
                        : shipToState.value?.country?.id || "",
                stateId:
                    cart.properties["shippingSelection"] === "alternate"
                        ? alternateShipAddress?.state?.id || ""
                        : shipToState.value?.state?.id || "",
                city:
                    cart.properties["shippingSelection"] === "alternate"
                        ? alternateShipAddress?.city || ""
                        : shipToState.value?.city || "",
                postalCode:
                    cart.properties["shippingSelection"] === "alternate"
                        ? alternateShipAddress?.postalCode || ""
                        : shipToState.value?.postalCode || "",
                fulfillmentMethod: cart.properties["isPickUp"] === "Y" ? "Pickup" : cart.fulfillmentMethod,
                onSuccess: (cartId: string) => {
                    localStorage.setItem("cartId", cartId);
                    if (!orderConfirmationPageLink) {
                        return;
                    }

                    if (cart?.isAwaitingApproval) {
                        resetCurrentCartId({});
                        toaster.addToast({
                            body: siteMessage("OrderApproval_OrderPlaced"),
                            messageType: "success",
                        });
                    }

                    history.push(`${orderConfirmationPageLink.url}?cartId=${cartId}`);
                },
                onError: (exception: any) => {
                    setIsAdonisDown(true);
                },
                onComplete(resultProps: any) {
                    if (resultProps.apiResult?.cart) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.apiResult.cart.id);
                    } else {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onError?.();
                    }
                },
            } as CustomParameter);
        } else {
            placeOrder({
                poNumber,
                reference: poReference,
                requestor: poRequestor,
                cart: updatedCartLineOject,
                properties: {
                    ...updatedCartLineOject.properties,
                    paymentMethod: paymentMethod === "Bill" ? "BILL" : "CC",
                    LTLLiftGateRequired: LTLLiftGateRequired,
                    LTLInSideDelivery: LTLInSideDelivery,
                    reference: poReference,
                    requestor: poRequestor,
                    job: poJob,
                    isPickUpAdiLocker: localCart?.properties?.isPickUpAdiLocker
                        ? localCart.properties.isPickUpAdiLocker
                        : cart.properties?.isPickUpAdiLocker,
                    willCallRush:
                        localCart?.properties.willCallRush === "Y"
                            ? localCart.properties.willCallRush
                            : cart.properties?.willCallRush,
                    adiLockInd: localCart?.properties?.adiLockInd
                        ? localCart.properties.adiLockInd
                        : cart.properties?.adiLockInd,
                    isPickUp: localCart?.properties?.isPickUp
                        ? localCart.properties.isPickUp
                        : cart.properties?.isPickUp,
                    //  p21BranchCode: cart?.p21BranchCode,
                    preferredSourceLocId: cart?.preferredSourceLocId,
                    thirdPartyOnFileId: "",
                    p21FreightCode: "",
                    isPONumberChanged: "false",
                },
                shipVia: localCart?.shipVia ? localCart.shipVia : cart?.shipVia,
                paymentMethod: paymentMethod === "Bill" ? "BILL" : "CC",
                saveCard: false,
                cardHolderName: "",
                selectedCCId: selectedSavedCard?.id || "",
                cardNumber: selectedSavedCard?.cardNumber || "",
                cardType: selectedSavedCard?.cardType || "",
                expirationMonth: Number(selectedSavedCard?.expiryMonth) || 0,
                expirationYear: Number(selectedSavedCard?.expiryMonth) || 0,
                securityCode: selectedSavedCard?.cardCVC || "",
                useBillingAddress: true,
                address1: shipToState.value?.address1 || "",
                countryId: shipToState.value?.country?.id || "",
                stateId: shipToState.value?.state?.id || "",
                city: shipToState.value?.city || "",
                postalCode: shipToState.value?.postalCode || "",
                fulfillmentMethod: cart.properties["isPickUp"] === "Y" ? "Pickup" : cart.fulfillmentMethod,
                pickUpDate: localCart?.properties?.pickUpDate
                    ? localCart.properties.pickUpDate
                    : cart.properties.pickupDate,
                onSuccess: (cartId: string) => {
                    if (!orderConfirmationPageLink) {
                        return;
                    }

                    if (cart?.isAwaitingApproval) {
                        resetCurrentCartId({});
                        toaster.addToast({
                            body: siteMessage("OrderApproval_OrderPlaced"),
                            messageType: "success",
                        });
                    }

                    history.push(`${orderConfirmationPageLink.url}?cartId=${cartId}`);
                },
                onError: (exception: any) => { },
                onComplete(resultProps: any) {
                    if (resultProps.apiResult?.cart) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.apiResult.cart.id);
                    } else {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onError?.();
                    }
                },
            } as CustomParameter);
        }
    };

    const displayBillTOOrder = () => {
        if (hideBillOrderDiv) {
            return null;
        } else if (!cart.ccRequired && !isCodCustomerNew(cartState.value)) {
            return <Radio value="Bill">{translate("Bill this Order")}</Radio>;
        } else if (isCodCustomerNew(cartState.value) && updatedDeliveryMethod === "pickup") {
            return <Radio value="Bill">{translate("Bill this Order")}</Radio>;
        } else {
            return null;
        }
    };

    return (
        <StyledWrapper
            {...styles.paymentMethodWrapper}
            id="leftSideDiv"
            style={{ height: `${leftSideDivHeight ? leftSideDivHeight : ""}px` }}
        >
            <StyledWrapper ref={targetRef}>
                <StyledForm
                    {...styles.form}
                    id="reviewAndSubmitPaymentForm"
                    noValidate={true}
                    data-test-selector="reviewAndSubmitPaymentForm"
                    onSubmit={handleSubmit}
                    method="POST"
                >
                    <StyledFieldSet {...styles.fieldset}>
                        <Typography as="h2" {...styles.heading}>
                            {translate("Payment Method")}
                        </Typography>

                        <Typography
                            as="p"
                            // {...styles.subHeading}
                            style={{
                                color: error ? `${ADIColors.darkBlack3}` : `${ADIColors.darkBlack3}`,
                                margin: "12px 0 13px",
                            }}
                        >
                            {translate("Please enter information in required fields")}
                        </Typography>
                        {adiPaymentCustomizationDto && (
                            <GridContainer {...styles.adipaymentDtoContainer}>
                                {adiPaymentCustomizationDto.po.visible && (
                                    <GridItem width={12} {...styles.zIndex}>
                                        <TextField
                                            label={translate("Purchase Order")}
                                            value={poNumber}
                                            name="poNumber"
                                            readOnly={adiPaymentCustomizationDto.po.isReadonly}
                                            onChange={onChangePoHandler}
                                            required={
                                                adiPaymentCustomizationDto.po.isRequired &&
                                                !adiPaymentCustomizationDto.po.isReadonly &&
                                                adiPaymentCustomizationDto.po.isRequired &&
                                                !adiPaymentCustomizationDto.po.isReadonly
                                            }
                                            error={
                                                error && (
                                                    <Typography as="h2" {...styles.TooltipBody}>
                                                        {poNumber.length <= 0 &&
                                                            adiPaymentCustomizationDto.po.isRequired ? (
                                                            <div>
                                                                <X
                                                                    style={{
                                                                        backgroundColor: `${ADIColors.red1}`,
                                                                        color: `${ADIColors.white}`,
                                                                        width: "10px",
                                                                        height: "10px",
                                                                        borderRadius: "50%",
                                                                        marginRight: "5px",
                                                                    }}
                                                                />
                                                                {"A valid Purchase Order number is required."}
                                                            </div>
                                                        ) : (
                                                            <Typography {...styles.disabledElement}>
                                                                {" "}
                                                                error message
                                                            </Typography>
                                                        )}
                                                        {adiPaymentCustomizationDto.po.syntax != null &&
                                                            adiPaymentCustomizationDto.po.syntax !== "" &&
                                                            syntaxVAlidation && (
                                                                <Typography>
                                                                    <Typography>
                                                                        <X
                                                                            style={{
                                                                                backgroundColor: `${ADIColors.red1}`,
                                                                                color: `${ADIColors.white}`,
                                                                                width: "10px",
                                                                                height: "10px",
                                                                                borderRadius: "50%",
                                                                                marginRight: "5px",
                                                                            }}
                                                                        />
                                                                    </Typography>
                                                                    Format
                                                                    <Tooltip
                                                                        iconProps={{
                                                                            size: 14,
                                                                            color: `${ADIColors.black1}`,
                                                                        }}
                                                                        cssOverrides={{
                                                                            tooltipBody: css`
                                                                                background: white !important;
                                                                                color: black !important;
                                                                                text-align: left;
                                                                            `,
                                                                            tooltipContainer: css`
                                                                                width: 120px;
                                                                                margin-left: -58px;
                                                                                &::before {
                                                                                    bottom: 100%;
                                                                                    top: -11px;
                                                                                }
                                                                            `,
                                                                            tooltipClickable: css`
                                                                                padding: 1px 4px 3px;
                                                                            `,
                                                                        }}
                                                                        onMouseOver={() => true}
                                                                        text={
                                                                            <StyledWrapper {...styles.Tooltiptext}>
                                                                                <strong>Format Key:</strong>
                                                                                <ul>
                                                                                    <li>
                                                                                        <strong>@</strong> Alpha
                                                                                    </li>
                                                                                    <li>
                                                                                        <strong>#</strong> Numeric
                                                                                    </li>
                                                                                    <li>
                                                                                        <strong>*</strong> AlphaNumeric
                                                                                    </li>
                                                                                    <li>
                                                                                        <strong>^</strong> Any Number of
                                                                                        Characters <br /> Other
                                                                                        Characters As Is
                                                                                    </li>
                                                                                </ul>
                                                                            </StyledWrapper>
                                                                        }
                                                                    ></Tooltip>
                                                                    :
                                                                    <Typography {...styles.Tooltiptext}>
                                                                        &nbsp;{syntaxVAlidation}
                                                                    </Typography>
                                                                </Typography>
                                                            )}
                                                    </Typography>
                                                )
                                            }
                                            maxLength={adiPaymentCustomizationDto.po.maxLength}
                                            placeholder={translate("Enter value here...")}
                                            {...inputBorde}
                                        />
                                    </GridItem>
                                )}
                                {adiPaymentCustomizationDto.reference.visible && (
                                    <GridItem width={12} {...styles.zIndex2}>
                                        <TextField
                                            label={translate("Reference Number")}
                                            value={poReference}
                                            name="poReference"
                                            readOnly={adiPaymentCustomizationDto.reference.isReadonly}
                                            onChange={onChangePoHandler}
                                            required={
                                                adiPaymentCustomizationDto.reference.isRequired &&
                                                !adiPaymentCustomizationDto.reference.isReadonly
                                            }
                                            maxLength={adiPaymentCustomizationDto.reference.maxLength}
                                            placeholder={translate("Enter value here...")}
                                            error={
                                                errorReference && (
                                                    <Typography {...styles.TooltipBody}>
                                                        <X
                                                            style={{
                                                                backgroundColor: `${ADIColors.red1}`,
                                                                color: `${ADIColors.white}`,
                                                                width: "10px",
                                                                height: "10px",
                                                                borderRadius: "50%",
                                                                marginRight: "5px",
                                                            }}
                                                        />{" "}
                                                        {errorReferenceMessage}
                                                    </Typography>
                                                )
                                            }
                                            {...inputBordeRef}
                                        />
                                    </GridItem>
                                )}
                                {adiPaymentCustomizationDto.job.visible && (
                                    <GridItem width={12}>
                                        <TextField
                                            label={translate("Job Name")}
                                            value={poJob}
                                            name="poJob"
                                            readOnly={adiPaymentCustomizationDto.job.isReadonly}
                                            onChange={onChangePoHandler}
                                            required={
                                                adiPaymentCustomizationDto.job.isRequired &&
                                                !adiPaymentCustomizationDto.job.isReadonly
                                            }
                                            maxLength={adiPaymentCustomizationDto.job.maxLength}
                                            placeholder={translate("Enter value here...")}
                                        />
                                    </GridItem>
                                )}
                            </GridContainer>
                        )}
                    </StyledFieldSet>
                    <button id="reviewAndSubmitPaymentForm-submit" type="submit" style={{ display: "none" }}>
                        {translate("Place Order")}
                    </button>
                </StyledForm>
                <StyledWrapper {...styles.billAndPayStyledWrapper}>
                    <GridContainer {...styles.billAndPayStyledInnerWrapper}>
                        <GridItem width={[12, 12, 12, 12, 6]}>
                            <RadioGroup
                                name="method"
                                onChangeHandler={e => onChangePaymentMethodHandler(e)}
                                value={paymentMethod}
                                {...styles.billAndPayRadioButton}
                            >
                                {displayBillTOOrder()}

                                {paymentMethod === "CC" && ccError && (
                                    <div style={{ marginBottom: "8px" }}>
                                        <Typography
                                            as="span"
                                            style={{
                                                color: `${ADIColors.danger}`,
                                            }}
                                        >
                                            {translate("Select a valid credit card to continue.")}
                                        </Typography>
                                    </div>
                                )}

                                {cart.allowCCPayment && (
                                    <Radio
                                        style={{
                                            display: cart.showCreditCard ? "inline-flex" : "none",
                                        }}
                                        value="CC"
                                    >
                                        <Typography
                                            as={"span"}
                                            style={{
                                                color:
                                                    paymentMethod === "CC" && ccError
                                                        ? `${ADIColors.danger}`
                                                        : `${ADIColors.black}`,
                                            }}
                                        >
                                            {translate("Pay by Credit Card")}
                                        </Typography>
                                    </Radio>
                                )}
                            </RadioGroup>
                        </GridItem>
                        {cart.allowCCPayment && (
                            <GridItem
                                width={[12, 12, 12, 12, 6]}
                                {...styles.mainPaymentGridItem}
                                style={{ display: cart.showCreditCard ? "inline-grid" : "none" }}
                            >
                                <ADICreditCardView
                                    item={selectedSavedCard}
                                    filterCardType={filterCardType}
                                    newCard={newCard}
                                />
                                {isFormError && (
                                    <div>
                                        <Typography
                                            as={"span"}
                                            style={{
                                                color: `${ADIColors.danger}`,
                                            }}
                                        >
                                            {translate("Credit Card Declined")}
                                        </Typography>
                                    </div>
                                )}

                                {selectedSavedCard && (
                                    <StyledWrapper {...styles.cardDisplyWithButton}>
                                        <div>
                                            <Link
                                                {...styles.rightLinks}
                                                onClick={() => setEditCard(selectedSavedCard, "fromSelectedCC", 1000)} // 100 for just to  differentiate the dit from model or selctedcard
                                            >
                                                {translate("Edit")}
                                            </Link>
                                            <Link {...styles.rightLinks} onClick={() => clearSelectedSavedCard()}>
                                                {translate("Clear")}
                                            </Link>
                                        </div>
                                    </StyledWrapper>
                                )}

                                <StyledWrapper {...styles.selectNewButtonsWrapper}>
                                    <Button variant="secondary" onClick={openNewCard} {...styles.secondaryButton}>
                                        {translate("New Card")}
                                    </Button>
                                    {savedCard && savedCard.creditCardDtoCollection.length > 0 && (
                                        <Button
                                            onClick={openGetCard}
                                            variant="primary"
                                            {...styles.primaryButton}
                                            disabled={!ispaymentOptionsLoaded}
                                        >
                                            {translate("Select Card")}
                                        </Button>
                                    )}
                                </StyledWrapper>
                            </GridItem>
                        )}
                    </GridContainer>
                    {/** Display Billing Address */}
                    {billToState.value && <ADIBillingAddressDisplay billToState={billToState.value} />}

                    {/* New Card */}
                    {/* <ADINewCreditCardModal
                        setFormError={setFormError}
                        setIsFromError={setIsFromError}
                        initialNewCardErrorState={initialNewCardErrorState}
                        closeNewCard={closeNewCard}
                        getApi={getApi}
                        setIsApiError={setIsApiError}
                        isNewCardOpen={newCreditCartPopup}
                        isFormError={isFormError}
                        isApiError={isApiError}
                        formError={formError}
                        cardTypes={cardTypes}
                        expirationMonths={expirationMonths}
                        expirationYears={expirationYears}
                        paginationModal={savedCard?.pagination}
                        handleNewCardDetails={handleNewCardDetails}
                    /> */}
                    {savedCard?.paymentTypes && (
                        <ADINewCreditCardModalPopup
                            setFormError={setFormError}
                            setIsFromError={setIsFromError}
                            initialNewCardErrorState={initialNewCardErrorState}
                            closeNewCard={closeNewCard}
                            getApi={getApi}
                            setIsApiError={setIsApiError}
                            isNewCardOpen={isNewCardOpen}
                            isFormError={isFormError}
                            isApiError={isApiError}
                            formError={formError}
                            cardTypes={cardTypes}
                            expirationMonths={expirationMonths}
                            expirationYears={expirationYears}
                            paginationModal={savedCard?.pagination}
                            handleNewCardDetails={handleNewCardDetails}
                            paymentTypes={savedCard}
                            showSuccessCallback={() => { }}
                            showErrorCallback={() => { }}
                        />
                    )}
                    {/* Get Card */}
                    {savedCard && (
                        <ADIGetCreditCardModal
                            savedCard={savedCard}
                            isGetCardOpen={isGetCardOpen}
                            closeGetCard={closeGetCard}
                            selectedSavedCardId={selectedSavedCardId}
                            handleCardChange={handleCardChange}
                            onChangePage={onChangePage}
                            setEditCard={setEditCard}
                            removeCard={removeCard}
                            filterCardType={filterCardType}
                            newCard={newCard}
                            IsRemoved={IsRemoved}
                        />
                    )}

                    {/* Edit Card */}
                    {savedEditCard && (
                        <ADIEditCreditCardModal
                            savedEditCard={savedEditCard}
                            isEditCardOpen={isEditCardOpen}
                            closeEditCard={closeEditCard}
                            getApi={getApi}
                            filterCardType={filterCardType}
                            expirationMonths={expirationMonths}
                            expirationYears={expirationYears}
                            setUpdateExpMonth={setUpdateExpMonth}
                            setUpdateExpYear={setUpdateExpYear}
                            updateExpMonth={updateExpMonth}
                            updateExpYear={updateExpYear}
                            paginationModal={savedCard?.pagination}
                            setSelectedSavedCard={setSelectedSavedCard}
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>

            <Modal
                {...styles.lTLPopupModal}
                headline={translate("LTLModelHeader")}
                isOpen={displayLTLModal}
                handleClose={closeLTLModal}
            >
                <StyledWrapper {...styles.lTlContentContainer}>
                    <StyledWrapper {...styles.lTlContent}>{translate("LTLModelContent")}</StyledWrapper>
                    <StyledWrapper {...styles.lTlBtnContainer}>
                        <Button {...styles.returnBtn} onClick={closeLTLModal}>
                            {translate("returnOrderBtn")}
                        </Button>
                        <Button {...styles.placeOrderBtn} onClick={placeOrderHandler}>
                            {translate("Place Order")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>

            {/* <Modal
                    isOpen={isAdonisDown}
                    {...styles.modal}
                >
                    <>
                        <Typography as="p" className="modalContainer">
                            <Icon src={WarningRedIcon} size={20} />{" "}
                            <Typography as="span" className="modalText">
                                {translate("There was an error processing your order. We apologize for the temporary interruption of service and are working on resolving the issue.")}
                            </Typography>
                        </Typography>
                    </>
                </Modal> */}
        </StyledWrapper>
    );
};
const widgetModule: WidgetModule = {
    component: connect(
        mapStateToProps,
        mapDispatchToProps,
    )(withToaster(withHistory(withTheme(ADICheckoutReviewAndSubmitPaymentDetails)))),
    definition: {
        group: "Checkout - Review & Submit",
        displayName: "ADI Payment Details",
        allowedContexts: [CheckoutReviewAndSubmitPageContext],
    },
};
export default widgetModule;
